import React, { useEffect, useState } from 'react';
import type { ApexOptions } from 'apexcharts';
import ChartBase from '../ChartBase';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { DatePickerField } from '../../ui/DatePickerField';
import { getAlarmStatisticsDetailed } from '../../../services/alarms/alarm.service';
import type { DetailedStatisticsResponse } from '../../../services/alarms/alarm.types';

const PATH_LIMIT = 8;

function toDateInputValue(value?: string | null): string {
  return value?.slice(0, 10) ?? '';
}

function validateFilters(startDate: string, endDate: string, topPathsLimit: number): string {
  if (!startDate || !endDate) {
    return 'Start date and end date are required.';
  }
  if (Date.parse(startDate) > Date.parse(endDate)) {
    return 'Start date must be on or before end date.';
  }
  if (!Number.isInteger(topPathsLimit) || topPathsLimit < 1) {
    return 'Top paths must be a positive whole number.';
  }
  return '';
}

function AlarmOutcomes({ total, cleared }: Readonly<{ total: number; cleared: number }>) {
  const options: ApexOptions = {
    labels: ['Clear', 'Other alarms'],
    colors: ['#00e396', '#0091ea'],
    legend: { position: 'bottom' },
    chart: { toolbar: { show: false } },
  };

  return <div className="col-12-xs col-6-md stretch"><div className="card"><div className="charts">
    <h3>Alarm outcomes</h3>
    {total > 0 && <ChartBase type="donut" id="detailedAlarmOutcomes" height={320} options={options} series={[cleared, total - cleared]} />}
    {total === 0 && <div className="table-responsive-nodata-text">No Data</div>}
  </div></div></div>;
}

type AlarmPath = NonNullable<DetailedStatisticsResponse['topAlarmPaths']>[number];

const STATE_LABELS: Record<string, string> = {
  '0': 'Normal',
  '1': 'Active',
  '2': 'Acknowledged',
  '3': 'Reset',
};

function priorityLabel(key: string): string {
  return `Priority ${key}`;
}

function stateLabel(key: string): string {
  return STATE_LABELS[key] ?? `State ${key}`;
}

function AlarmBreakdown({ counts, title, id, color, labelForId }: Readonly<{
  counts?: Record<string, number>;
  title: string;
  id: string;
  color: string;
  labelForId: (value: string) => string;
}>) {
  const entries = Object.entries(counts ?? {})
    .filter(([, count]) => Number.isFinite(count) && count > 0)
    .sort(([first], [second]) => Number(first) - Number(second));
  const options: ApexOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 2 } },
    xaxis: { categories: entries.map(([key]) => labelForId(key)) },
    dataLabels: { enabled: true, formatter: value => Number(value).toLocaleString() },
    colors: [color],
  };

  return <div className="col-12-xs col-6-md stretch"><div className="card"><div className="charts">
    <h3>{title}</h3>
    {entries.length > 0 && <ChartBase type="bar" id={id} height={320} options={options} series={[{ name: 'Alarms', data: entries.map(([, count]) => count) }]} />}
    {entries.length === 0 && <div className="table-responsive-nodata-text">No Data</div>}
  </div></div></div>;
}

function TopAlarmPaths({ paths }: Readonly<{ paths: AlarmPath[] }>) {
  const options: ApexOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 2 } },
    xaxis: { categories: paths.map(path => path.pathToAlarm || `Path ${path.alarmPathId ?? ''}`) },
    yaxis: { labels: { maxWidth: 180 } },
    dataLabels: { enabled: false },
    colors: ['#0091ea'],
  };

  return <div className="col-12-xs col-6-md stretch"><div className="card"><div className="charts">
    <h3>Top alarm paths</h3>
    {paths.length > 0 && <ChartBase type="bar" id="detailedAlarmPaths" height={320} options={options} series={[{ name: 'Alarms', data: paths.map(path => path.alarmCount ?? 0) }]} />}
    {paths.length === 0 && <div className="table-responsive-nodata-text">No Data</div>}
  </div></div></div>;
}

type StatisticsFilters = { startDate?: string; endDate?: string; topPathsLimit: number };

function useDetailedStatistics(
  appliedFilters: StatisticsFilters,
  setStartDate: React.Dispatch<React.SetStateAction<string>>,
  setEndDate: React.Dispatch<React.SetStateAction<string>>
) {
  const [stats, setStats] = useState<DetailedStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(false);
    getAlarmStatisticsDetailed(appliedFilters)
      .then(response => {
        if (canceled) { return; }
        if (response.success && response.data) {
          setStats(response.data);
          setStartDate(current => current || toDateInputValue(response.data.startDate));
          setEndDate(current => current || toDateInputValue(response.data.endDate));
        } else {
          setError(true);
        }
      })
      .catch(() => { if (!canceled) { setError(true); } })
      .finally(() => { if (!canceled) { setLoading(false); } });
    return () => { canceled = true; };
  }, [appliedFilters, setStartDate, setEndDate]);

  return { stats, loading, error };
}

function StatisticsCharts({ stats, limit }: Readonly<{ stats: DetailedStatisticsResponse | null; limit: number }>) {
  const total = Math.max(0, stats?.totalAlarms ?? 0);
  const cleared = Math.min(total, Math.max(0, stats?.clearCount ?? 0));
  const paths = (stats?.topAlarmPaths ?? [])
    .filter(path => (path.alarmCount ?? 0) > 0)
    .slice(0, limit);

  return <div className="row gap-1 mb-2">
    <AlarmOutcomes total={total} cleared={cleared} />
    <TopAlarmPaths paths={paths} />
    <AlarmBreakdown counts={stats?.alarmsByPriority} title="Alarms by priority" id="detailedAlarmPriorities" color="#ff4560" labelForId={priorityLabel} />
    <AlarmBreakdown counts={stats?.alarmsByState} title="Alarms by state" id="detailedAlarmStates" color="#00e396" labelForId={stateLabel} />
  </div>;
}

const DetailedAlarmCharts: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [topPathsLimit, setTopPathsLimit] = useState(PATH_LIMIT);
  const [appliedFilters, setAppliedFilters] = useState<StatisticsFilters>({ topPathsLimit: PATH_LIMIT });
  const [filterError, setFilterError] = useState('');
  const { stats, loading, error } = useDetailedStatistics(appliedFilters, setStartDate, setEndDate);

  const handleApplyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateFilters(startDate, endDate, topPathsLimit);
    setFilterError(validationError);
    if (validationError) {
      return;
    }
    setAppliedFilters({
      startDate: `${startDate}T00:00:00.000Z`,
      endDate: `${endDate}T23:59:59.999Z`,
      topPathsLimit,
    });
  };

  return (
    <section aria-label="Detailed alarm statistics">
      <div className="alarm-content mb-1 pb-1">
      <form className="detailed-alarm-filters" onSubmit={handleApplyFilters}>
        <DatePickerField className="detailed-alarm-filters__field detailed-alarm-filters__date" label="Start date" name="alarm-stats-start" value={startDate} onChange={setStartDate} />
        <DatePickerField className="detailed-alarm-filters__field detailed-alarm-filters__date" label="End date" name="alarm-stats-end" value={endDate} onChange={setEndDate} />
        <div className="detailed-alarm-filters__field">
          <label className="form-field__label" htmlFor="alarm-stats-path-limit">Top paths</label>
          <input className="form-field__input" id="alarm-stats-path-limit" type="number" min="1" step="1" required value={topPathsLimit} onChange={event => setTopPathsLimit(Number(event.target.value))} />
        </div>
        <div className="detailed-alarm-filters__actions">
          <button type="submit" className="btn btn-apply" disabled={loading}>Apply</button>
        </div>
      </form>
      </div>
      {filterError && <p role="alert" className="status__error">{filterError}</p>}
      {loading && <LoadingSpinner message="Loading alarm statistics..." />}
      {error && <p className="status__error">Unable to load alarm statistics.</p>}
      {!loading && !error && <StatisticsCharts stats={stats} limit={appliedFilters.topPathsLimit} />}
    </section>
  );
};

export default DetailedAlarmCharts;